<template>
  <v-card>
    <v-card-title class="d-flex align-center justify-space-between">
      <div class="d-flex align-center">
        <v-icon left>mdi-account-multiple</v-icon>
        <span>Estudiantes en la Sesión</span>
        <v-chip
          color="primary"
          size="small"
          class="ml-3"
        >
          {{ participants.length }}
        </v-chip>
      </div>
      
      <!-- Refresh button -->
      <v-btn
        icon="mdi-refresh"
        variant="text"
        size="small"
        :loading="loading"
        @click="refreshData"
      />
    </v-card-title>

    <!-- Filters -->
    <v-card-text class="pb-0">
      <v-row>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="searchQuery"
            label="Buscar estudiantes"
            prepend-inner-icon="mdi-magnify"
            variant="outlined"
            density="compact"
            clearable
            hide-details
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-select
            v-model="statusFilter"
            label="Filtrar por estado"
            :items="statusOptions"
            variant="outlined"
            density="compact"
            clearable
            hide-details
          />
        </v-col>
      </v-row>
    </v-card-text>

    <v-card-text>
      <!-- Empty state -->
      <div v-if="filteredParticipants.length === 0 && !loading" class="text-center py-8">
        <v-icon size="64" color="grey-lighten-1">mdi-account-off</v-icon>
        <div class="text-h6 mt-3 text-medium-emphasis">
          {{ participants.length === 0 ? 'No hay estudiantes en la sesión' : 'No se encontraron estudiantes' }}
        </div>
        <div class="text-body-2 text-medium-emphasis">
          {{ participants.length === 0 ? 'Los estudiantes aparecerán aquí cuando se unan' : 'Intenta ajustar los filtros' }}
        </div>
      </div>

      <!-- Students table -->
      <v-data-table
        v-else
        :headers="headers"
        :items="filteredParticipants"
        :loading="loading"
        :items-per-page="10"
        class="students-table"
        loading-text="Cargando estudiantes..."
        no-data-text="No hay estudiantes"
      >
        <!-- Status column -->
        <template #item.status="{ item }">
          <v-chip
            :color="getStatusColor(item.status)"
            size="small"
            variant="tonal"
          >
            <v-icon left size="small">{{ getStatusIcon(item.status) }}</v-icon>
            {{ getStatusText(item.status) }}
          </v-chip>
        </template>

        <!-- Join time column -->
        <template #item.joined_at="{ item }">
          <span class="text-body-2">{{ formatDateTime(item.joined_at) }}</span>
        </template>

        <!-- Actions column -->
        <template #item.actions="{ item }">
          <div class="d-flex ga-1">
            <!-- Remove student button -->
            <v-btn
              icon="mdi-account-remove"
              variant="text"
              size="small"
              color="error"
              :disabled="loading || item.status === 'left'"
              @click="confirmRemoveStudent(item)"
            >
              <v-icon>mdi-account-remove</v-icon>
              <v-tooltip activator="parent" location="top">
                Remover estudiante
              </v-tooltip>
            </v-btn>

            <!-- View details button -->
            <v-btn
              icon="mdi-information"
              variant="text"
              size="small"
              color="primary"
              @click="showStudentDetails(item)"
            >
              <v-icon>mdi-information</v-icon>
              <v-tooltip activator="parent" location="top">
                Ver detalles
              </v-tooltip>
            </v-btn>
          </div>
        </template>
      </v-data-table>
    </v-card-text>

    <!-- Remove student confirmation dialog -->
    <v-dialog v-model="showRemoveDialog" max-width="400">
      <v-card>
        <v-card-title class="text-h6">
          ¿Remover estudiante?
        </v-card-title>
        <v-card-text>
          ¿Estás seguro de que quieres remover a 
          <strong>{{ selectedStudent?.student_name }}</strong> de la sesión?
          <br><br>
          El estudiante será marcado como "abandonó" y no podrá continuar.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="grey"
            variant="text"
            @click="showRemoveDialog = false"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="error"
            variant="tonal"
            :loading="loading"
            @click="removeStudent"
          >
            Remover
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Student details dialog -->
    <v-dialog v-model="showDetailsDialog" max-width="500">
      <v-card v-if="selectedStudent">
        <v-card-title class="d-flex align-center">
          <v-avatar color="primary" class="mr-3">
            <v-icon>mdi-account</v-icon>
          </v-avatar>
          {{ selectedStudent.student_name }}
        </v-card-title>
        <v-card-text>
          <div class="d-flex flex-column ga-3">
            <div>
              <div class="text-subtitle-2 text-medium-emphasis">Correo</div>
              <div>{{ selectedStudent.student_email }}</div>
            </div>
            <div>
              <div class="text-subtitle-2 text-medium-emphasis">Estado</div>
              <v-chip
                :color="getStatusColor(selectedStudent.status)"
                size="small"
                variant="tonal"
              >
                {{ getStatusText(selectedStudent.status) }}
              </v-chip>
            </div>
            <div>
              <div class="text-subtitle-2 text-medium-emphasis">Se unió</div>
              <div>{{ formatDateTime(selectedStudent.joined_at) }}</div>
            </div>
            <div v-if="selectedStudent.left_at">
              <div class="text-subtitle-2 text-medium-emphasis">Abandonó</div>
              <div>{{ formatDateTime(selectedStudent.left_at) }}</div>
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="primary"
            variant="text"
            @click="showDetailsDialog = false"
          >
            Cerrar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'

const store = useStore()

// Reactive data
const searchQuery = ref('')
const statusFilter = ref(null)
const showRemoveDialog = ref(false)
const showDetailsDialog = ref(false)
const selectedStudent = ref(null)

// Computed properties
const participants = computed(() => store.getters['session/getParticipants'])
const loading = computed(() => store.getters['session/isLoading'])

const statusOptions = [
  { title: 'Esperando', value: 'waiting' },
  { title: 'Activo', value: 'active' },
  { title: 'Completado', value: 'completed' },
  { title: 'Abandonó', value: 'left' }
]

const headers = [
  { title: 'Nombre', key: 'student_name', sortable: true },
  { title: 'Correo', key: 'student_email', sortable: true },
  { title: 'Estado', key: 'status', sortable: true },
  { title: 'Se unió', key: 'joined_at', sortable: true },
  { title: 'Acciones', key: 'actions', sortable: false, align: 'center', width: 120 }
]

const filteredParticipants = computed(() => {
  let filtered = participants.value

  // Filter by search query
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    filtered = filtered.filter(participant =>
      participant.student_name?.toLowerCase().includes(query) ||
      participant.student_email?.toLowerCase().includes(query)
    )
  }

  // Filter by status
  if (statusFilter.value) {
    filtered = filtered.filter(participant => participant.status === statusFilter.value)
  }

  return filtered
})

// Methods
const getStatusColor = (status) => {
  switch (status) {
    case 'waiting': return 'orange'
    case 'active': return 'success'
    case 'completed': return 'info'
    case 'left': return 'error'
    default: return 'grey'
  }
}

const getStatusIcon = (status) => {
  switch (status) {
    case 'waiting': return 'mdi-clock-time-four-outline'
    case 'active': return 'mdi-play'
    case 'completed': return 'mdi-check-circle'
    case 'left': return 'mdi-exit-to-app'
    default: return 'mdi-help-circle'
  }
}

const getStatusText = (status) => {
  switch (status) {
    case 'waiting': return 'Esperando'
    case 'active': return 'Activo'
    case 'completed': return 'Completado'
    case 'left': return 'Abandonó'
    default: return 'Desconocido'
  }
}

const formatDateTime = (dateString) => {
  if (!dateString) return '-'
  return new Date(dateString).toLocaleString('es-ES', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const confirmRemoveStudent = (student) => {
  selectedStudent.value = student
  showRemoveDialog.value = true
}

const removeStudent = async () => {
  if (!selectedStudent.value) return

  const result = await store.dispatch('session/removeStudent', selectedStudent.value.id)
  
  if (result.success) {
    const studentName = selectedStudent.value.student_name
    showRemoveDialog.value = false
    selectedStudent.value = null
    
    // Emit success event to parent
    emit('student-removed', studentName)
  }
}

const showStudentDetails = (student) => {
  selectedStudent.value = student
  showDetailsDialog.value = true
}

const refreshData = () => {
  // Trigger refresh from parent component
  emit('refresh-requested')
}

// Emits
const emit = defineEmits(['student-removed', 'refresh-requested'])

// Lifecycle
onMounted(() => {
  // Component is ready
})
</script>

<style scoped>
.students-table :deep(.v-data-table-header) {
  background-color: #f5f5f5;
}

.students-table :deep(.v-data-table-row:hover) {
  background-color: #f9f9f9;
}

@media (max-width: 768px) {
  .students-table :deep(.v-data-table) {
    font-size: 0.875rem;
  }
}
</style>